// ─── Bandeau publicitaire (Core) ──────────────────────────────────
// Levé de MoPCal (App.js `PubZone`). Piloté par le MÊME doc admin `config/pub` :
// `banniereFixe.actif` (absent = affiché) et `banniereFixe.url` (lien au clic).
// L'app hôte décide en amont si elle l'affiche (selon son forfait / « sans pub »).
//
// AdMob (20/08) : sur natif (iOS/Android), un VRAI bandeau AdMob remplace le
// simulateur — conformité Google Play / Apple (une app ne doit pas afficher de
// pseudo-publicité). Sur le web, `react-native-google-mobile-ads` n'existe pas
// (le SDK est natif uniquement) : le simulateur reste affiché tel quel. Une app
// qui n'a pas encore installé le module (pas encore migrée) retombe aussi sur
// le simulateur — `require` protégé, jamais de crash au bundle.
// ID d'unité : `config/pub.admob.{android,ios}AdUnitId` — à défaut, les ID de
// TEST officiels de Google (sûrs à afficher, jamais de vraie pub ni de revenu).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking, Platform } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { ecouterConfigPub } from '../services/config';
import { useT } from '../i18n/LangueContext';

export const PUB_HEIGHT = 50;
const PUB_URL_DEMO = 'https://mopsuite.fr';

let AdMob = null;
if (Platform.OS !== 'web') {
  try { AdMob = require('react-native-google-mobile-ads'); } catch { /* module absent : app pas encore migrée */ }
}

export default function PubZone() {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [actif, setActif] = useState(true);   // affiché tant que l'admin ne coupe pas
  const [url, setUrl]     = useState('');
  const [admob, setAdmob] = useState(null);   // { androidAdUnitId, iosAdUnitId }

  useEffect(() => {
    const unsub = ecouterConfigPub(data => {
      setActif(data?.banniereFixe?.actif !== false);
      setUrl((data?.banniereFixe?.url || '').trim());
      setAdmob(data?.admob || null);
    });
    return () => unsub();
  }, []);

  if (!actif) return null;

  if (AdMob) {
    const { BannerAd, BannerAdSize, TestIds } = AdMob;
    const unitId = Platform.select({
      android: admob?.androidAdUnitId || TestIds.BANNER,
      ios: admob?.iosAdUnitId || TestIds.BANNER,
      default: TestIds.BANNER,
    });
    return (
      <View style={s.pubZoneAdmob}>
        <BannerAd unitId={unitId} size={BannerAdSize.BANNER} />
      </View>
    );
  }

  // Simulateur (web, ou app pas encore migrée vers AdMob).
  const ouvrir = () => { Linking.openURL(url || PUB_URL_DEMO).catch(() => {}); };
  return (
    <TouchableOpacity style={s.pubZone} onPress={ouvrir} activeOpacity={0.85} accessibilityLabel={t('pub.accessibilite')}>
      <Text style={s.pubTag}>{t('pub.annonce')}</Text>
      <Text style={s.pubIcone}>🛍️</Text>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={s.pubTitre} numberOfLines={1}>{t('pub.titreBanniere')}</Text>
        <Text style={s.pubSous} numberOfLines={1}>{t('pub.sousBanniere')}</Text>
      </View>
      <View style={s.pubCta}><Text style={s.pubCtaTxt}>{t('aide.voir')}</Text></View>
    </TouchableOpacity>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  pubZoneAdmob: {
    height: PUB_HEIGHT, alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    backgroundColor: UI.card, borderTopWidth: 1, borderTopColor: UI.border,
  },
  pubZone: {
    height: PUB_HEIGHT, flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 12, backgroundColor: UI.card,
    borderTopWidth: 1, borderTopColor: UI.border,
  },
  pubTag: {
    fontSize: 9, fontWeight: '700', color: UI.muted, borderWidth: 1, borderColor: UI.border,
    borderRadius: 4, paddingHorizontal: 4, paddingVertical: 1, textTransform: 'uppercase',
  },
  pubIcone: { fontSize: 18 },
  pubTitre: { fontSize: 13, fontWeight: '700', color: UI.ink },
  pubSous:  { fontSize: 11, color: UI.muted },
  pubCta:   { backgroundColor: UI.accent, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  pubCtaTxt:{ color: '#fff', fontSize: 12, fontWeight: '700' },
});
